import type { SelectedEnvironment, SelectedProject } from '@gonfalon/environments/ProjectEnvironmentSelector';
import { type SdkKey } from '@gonfalon/sdk-info/sdkInfo';
import type { SdkInitKeys } from '@gonfalon/sdk-info/types';
import Prism from 'prismjs';

export type { SdkInitKeys };

export const languages = Object.keys(Prism.languages);
export type Product = 'flags' | 'experiments' | 'ai-configs' | 'observability';
export type AIConfigSdkChoice = 'python' | 'javascript' | 'dotnet' | 'go' | 'ruby';

export type SnippetLang = (typeof languages)[number];

export type OnboardingFlag = {
  flagKey: string;
  flagName: string;
  flagOn: boolean;
};

export type OnboardingAiConfig = {
  aiConfigName: string;
  aiConfigKey: string;
};

export type OnboardingAiConfigVariation = {
  variationKey: string;
  variationName: string;
};

export type AnalyticAttributes = { component: string; path: string; url: string; type: string; cta?: string };

export type OnboardingAnalytics = {
  trackAiConfigVariationMessagesUpdated: (role: string) => void;
  trackDocumentationLinkClicked: (targetUrl: string, analyticsAttributes: AnalyticAttributes) => void;
  trackOnboardingEvent: (eventName: string) => void;
  trackOnboardingButtonClicked: (buttonLabel: string) => void;
  trackOnboardingLinkClicked: (linkLabel: string) => void;
  trackOnboardingPathClicked: (path: Product) => void;
  trackOnboardingSdkSelected: (sdkKebab: SdkKey) => void;
  trackOnboardingLinkToSdkKeysClicked: () => void;
  trackOnboardingStepExpanded: (product: Product, componentName: string, expanded: boolean) => void;
  trackCursorSdkInstallButtonClicked: () => void;
  trackOnboardingSdkInstalled: (projectKey: string, environmentKey: string, sdk: SdkKey) => void;
  trackError: (error: Error) => void;
};

export type OnboardingRootProps = {
  analytics: OnboardingAnalytics;
  canInviteTeammates: boolean;
  detectSdkActive: boolean;
  enableAiConfigs: boolean;
  enableCursorLinks: boolean;
  enableObservability: boolean;
  isAuthenticated?: boolean;
  memberId: string;
  projectEnvironmentsPath: string;
  sdkInitKeys: SdkInitKeys;
  selectedProject: SelectedProject | null;
  selectedEnvironment: SelectedEnvironment | null;
};

export type StorageType = {
  sdk: SdkKey;
  product: Product;
  isLoadingSdkKeys: boolean;
} & Omit<OnboardingRootProps, 'analytics'>;

export type AndroidInstallSnippetType = {
  groovy: string;
  kotlin: string;
};

export type DotnetServerInstallSnippetType = {
  csharp: string;
};

export type GoInstallSnippetType = {
  go: string;
};

export type iosInstallSnippetType = {
  cartfile: string;
  packageSwift: string;
  podfile: string;
};

export type JavaInstallSnippetType = {
  gradle: string;
  xml: string;
};

export type JsInstallSnippetType = {
  npm: string;
  pnpm: string;
  yarn: string;
};

export type NodeServerInstallSnippetType = {
  npm: string;
  pnpm: string;
  yarn: string;
};

export type PythonInstallSnippetType = {
  pip: string;
};

export type ReactWebInstallSnippetType = {
  npm: string;
  pnpm: string;
  yarn: string;
};

export type ReactNativeInstallSnippetType = {
  npm: string;
  pnpm: string;
  yarn: string;
  asyncStorage: {
    npm: string;
    pnpm: string;
    yarn: string;
  };
};
